import { json, type GeneratedFile } from './files';

export interface PrintServerInput {
  /** `{PrintQueueUrl}/api/printqueue/v1`. */
  printQueueUri: string;
  printServerName: string;
  /** Left out with an AUTH client only: the print server asks the print queue (print server 2.2.0+). */
  tenantId?: string;
  /** Legacy print queue token (role PrintServer). */
  token?: string;
  /** AUTH client (scope `printqueue:print`). */
  auth?: { clientId: string; clientSecret: string };
}

/** The site keys of a print server; `install.ps1` overlays them on the packaged defaults (S1). */
export function printServerSettings(p: PrintServerInput): Record<string, unknown> {
  return {
    PrintQueueUri: p.printQueueUri,
    PrintServerName: p.printServerName,
    ...(p.tenantId ? { TenantId: p.tenantId } : {}),
    ...(p.token ? { Token: p.token } : {}),
    ...(p.auth ? { Auth: { ClientId: p.auth.clientId, ClientSecret: p.auth.clientSecret } } : {}),
  };
}

export const printServerInstallPs1 = `# In an elevated PowerShell, in the folder of the unpacked nycelogicprintserver-…-win-x64.zip
# with this appsettings.json next to install.ps1:
Set-ExecutionPolicy -Scope Process Bypass
.\\install.ps1 -ConfigFile .\\appsettings.json
# Later versions: Operations Hub (Print servers → Update), or .\\install.ps1 from a newer zip   Remove: .\\install.ps1 -Uninstall
`;

export function printServerCompose(image: string): string {
  return `# NYCE.LOGIC Print Server with CUPS. Next to nycelogicprintserver.json:  docker compose up -d
services:
  printserver:
    image: ${image}
    container_name: nycelogic-printserver
    restart: unless-stopped
    # running prints finish when stopping; the bootstrapper gives the print server 45 s
    stop_grace_period: 60s
    volumes:
      - ./nycelogicprintserver.json:/config/nycelogicprintserver.json:ro
      - ./log:/log
      # versions installed by updates from the Operations Hub, kept when the container is recreated
      - ./data:/data
    environment:
      # The CUPS server that owns the printers.
      CUPS_SERVER_URI: \${CUPS_SERVER_URI:-http://host.docker.internal:631}
      CUPS_USERNAME: \${CUPS_USERNAME:-}
      CUPS_PASSWORD: \${CUPS_PASSWORD:-}
    extra_hosts:
      - host.docker.internal:host-gateway
`;
}

/** Files for one print server on Windows (service) or Linux/macOS (CUPS container). */
export function printServerFiles(
  p: PrintServerInput,
  platform: 'windows' | 'cups',
  cupsImage: string
): GeneratedFile[] {
  const settings = json(printServerSettings(p));
  if (platform === 'windows')
    return [
      {
        name: 'appsettings.json',
        content: settings,
        note: 'Put it next to install.ps1 in the unpacked release zip.',
      },
      {
        name: 'install.txt',
        content: printServerInstallPs1,
        note: 'Run on the print server (as administrator).',
      },
    ];
  return [
    {
      name: 'nycelogicprintserver.json',
      content: settings,
      note: 'Mounted read-only as /config/nycelogicprintserver.json.',
    },
    { name: 'docker-compose.yml', content: printServerCompose(cupsImage), note: 'docker compose up -d' },
  ];
}
